import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faChevronDown,
  faEllipsisV,
  faEnvelope,
  faTrash,
  faXmarkCircle,
} from "@fortawesome/free-solid-svg-icons";
import { chaseTableActions, enhancedloanStatusData } from "./chase-list-data";
import React, { Fragment, useState } from "react";
import { useChaseMenu } from "../../store/use-chase-menu";
import ChaseDropMenu from "./chase-drop-menu";
import { Menu, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import { ParseErrors } from "../../utils/parse-errors";
import axiosInstance from "../../utils/axios-instance";
import DeleteModel from "../../models/delete-model";
import { useDeleteModel } from "../../store/use-delete-model";
import EditChaseModel from "../../models/chase-edit-model";
import { useChaseEditModel } from "../../store/use-chase-edit-model";
import moment from "moment";
import GetUser from "../../utils/get-user";
import NotesModal from "../../models/notes-model";
import { useSuretyModel } from "../../store/use-surety-popup";
import PopupSuretyModel from "../../models/popup-surety";
import IsChaseOverSix from "../../utils/is-chase-over-six";

interface TChaseRenderitem {
  item: any;
  index: number;
}

const getTemporaryStatus = ({
  log,
  status,
  item,
}: {
  log?: { attempt: number; paymentPlan: string };
  status: string;
  item: Record<string, any>;
}) => {
  if (status === "Awaiting-Customer" && item?.partialPaymentAmount > 0) {
    return "Partial";
  } else if (
    log?.attempt < Number(log?.paymentPlan?.split("-")[0]) &&
    status === "On-Hold"
  ) {
    return "Plan";
  } else {
    return status;
  }
};

const getStatus = (status: string) => {
  const statusData = enhancedloanStatusData.find(
    (stat) => stat.name.toLowerCase() === (status || "").toLowerCase()
  );
  return statusData;
};

const ChaseRenderitem: React.FC<TChaseRenderitem> = ({
  item: itemData,
  index,
}) => {
  const user = GetUser();
  const [item, setItem] = useState<Record<string, any> | null>(itemData);
  const [isMessagePopup, setIsMessagePopup] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { initialValues, onToggleChaseMenu, onChaseMenuOpen } = useChaseMenu();
  const {
    initialValues: deletePopupInitialValues,
    onOpenDeletePopup,
    onCloseDeletePopup,
  } = useDeleteModel();
  const {
    onOpenSuretyPopup,
    onCloseSuretyPopup,
    initialValues: suretyInitialValues,
  } = useSuretyModel();
  const { initialValues: editInitialValues, onOpen: onEditOpen } =
    useChaseEditModel();
  let isTemporaryStatus = getTemporaryStatus({
    log: item?.log,
    status: item?.status,
    item: itemData,
  });
  const lastDate = new Date(item?.startDate);
  lastDate.setDate(lastDate.getDate() + 30);

  // --------------------------- [ DELETE CHASE ] ------------------------------
  const HandleDeleteChase = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.delete(`/loan/${item?.id}`);
      onCloseDeletePopup();
      setLoading(false);
      setItem(null);
      toast.success(res.data?.message);
    } catch (error) {
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  // --------------------------- [ TOGGLE ARCHIVE ] ------------------------------
  const HandletoggleArchive = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post(`/loan/toggle/archive/${item?.id}`);
      onCloseSuretyPopup();
      setLoading(false);
      setItem(null);
      toast.success(res.data?.message);
    } catch (error) {
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  // --------------------------- [ HANDLE ON EDIT CALL ] ------------------------------
  const HandleEditChase = () => {
    onEditOpen({ id: item?.id, data: item });
  };

  // ------------------------------- [ UPDATE ITEM DATA ] ------------------------------
  const UpdateItemData = async () => {
    try {
      const res = await axiosInstance.get(`/loan?id=${item?.id}`);
      const resData = res.data?.data[0];
      onChaseMenuOpen({
        id: resData?.id,
        data: resData,
        timeline: resData?.timeline,
        logs: resData?.logs,
      });
      setItem(resData);
    } catch (error) {
      console.log(error);
    }
  };

  // ------------------------------- [ CANCEL PAYMENT PLAN ] ------------------------------
  const CancelPaymentPlan = async () => {
    try {
      const res = await axiosInstance.post(`/loan/cancel/plan/${item?.id}`);
      toast.success(res.data?.message);
      UpdateItemData();
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
    }
  };

  // ------------------------------- [ REMOVE CHASE FROM ICB ] ------------------------------
  const RemoveChaseFromICB = async () => {
    try {
      const res = await axiosInstance.post(`/loan/cancel/icb/${item?.id}`);
      toast.success(res.data?.message);
      UpdateItemData();
    } catch (error) {
      console.log(error);
      toast.error(ParseErrors(error));
    }
  };

  const ToggleMessagesPopup = () => {
    setIsMessagePopup(!isMessagePopup);
  };

  if (!item) {
    return null;
  }

  return (
    <>
      <tr
        className={clsx(
          "relative font-medium border-b border-edge-faint transition-colors duration-200 hover:bg-overlay-hover",
        )}
        key={index}
      >
        <td className="w-[18%] py-3.5 pl-1 align-top">
          <p className="text-[14px] font-semibold text-text-primary">
            {item?.isPremium ? "👑" : null}{" "}
            {item?.firstName + " " + item.surname}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray">
            {item?.policyReference}
          </p>
        </td>
        <td className="py-3.5 w-[22%] align-top">
          <p className="text-[13px] text-text-primary break-all">
            {item?.email}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray tabular-nums">
            {item?.phone}
          </p>
        </td>
        <td className="py-3.5 w-[12%] align-top">
          <p className="text-[13px] text-text-primary whitespace-nowrap">
            {moment(item?.createdAt).format("DD-MM-YYYY")}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray whitespace-nowrap">
            {moment(item?.lastChanceDate).format("DD-MM-YYYY")}
          </p>
        </td>
        <td className="py-3.5 w-[9%] align-center">
          <p className="text-[13px] text-text-primary !capitalize">
            {item?.chaseType}
          </p>
        </td>
        <td className="py-3.5 w-[11%] align-top">
          <p className="text-[12px] text-light-gray !capitalize">Outstanding</p>
          <p className="mt-0.5 text-[14px] font-bold text-text-primary tabular-nums whitespace-nowrap">
            £{item.outstandingBalance?.toFixed(2)}
          </p>
        </td>
        <td className="py-3.5 text-light-gray w-[10%] align-middle space-y-1.5">
          <div className="flex items-center justify-start gap-2.5">
            <svg
              className="text-light-gray"
              width="17"
              height="8"
              viewBox="0 0 13 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.7737 1.83211C12.6287 1.47483 12.4213 1.15404 12.1571 0.878685C11.893 0.60333 11.5853 0.387093 11.2425 0.235949C10.8874 0.0793867 10.5105 0 10.1222 0C9.73206 0 9.28858 0.148983 8.76648 0.455412C8.3876 0.677772 7.96757 0.984659 7.51805 1.36758C7.10497 1.71944 5.27274 3.56458 4.88879 3.89113C4.48161 4.23744 4.10705 4.51149 3.77551 4.70565C3.31113 4.97764 3.031 5.03472 2.87776 5.03472C1.80152 5.03472 0.925911 4.12195 0.925911 2.99998C0.925911 1.87805 1.80152 0.965259 2.87776 0.965259C3.031 0.965259 3.31113 1.02235 3.77551 1.29435C4.10704 1.48852 4.48161 1.76256 4.88881 2.10886C5.11855 2.30425 5.33272 2.50081 5.51749 2.67751L5.82796 2.33601C5.84157 2.32102 5.96437 2.18649 6.16319 1.98477C5.96506 1.79483 5.73335 1.58175 5.48195 1.36761C5.03241 0.984693 4.6124 0.677805 4.23352 0.455444C3.7114 0.148999 3.26794 3.29752e-05 2.87777 3.29752e-05C2.48949 3.29752e-05 2.11255 0.0794355 1.75747 0.235982C1.41474 0.387109 1.10702 0.603346 0.842895 0.878718C0.578755 1.15407 0.371325 1.47486 0.226354 1.83214C0.0761685 2.20229 0 2.59523 0 3C0 3.40479 0.0761685 3.79773 0.226354 4.16789C0.37131 4.52517 0.578739 4.84596 0.842895 5.12131C1.10702 5.39667 1.41474 5.61291 1.75747 5.76405C2.11255 5.92061 2.48949 6 2.87777 6C3.26794 6 3.71142 5.85102 4.23352 5.54459C4.6124 5.32221 5.03241 5.01531 5.48195 4.63242C5.89503 4.28056 7.72726 2.43541 8.11119 2.10886C8.51839 1.76256 8.89295 1.48852 9.22449 1.29435C9.68887 1.02236 9.969 0.965259 10.1222 0.965259C11.1985 0.965259 12.0741 1.87805 12.0741 2.99998C12.0741 4.12195 11.1985 5.03472 10.1222 5.03472C9.969 5.03472 9.68887 4.97765 9.22449 4.70565C8.89296 4.51149 8.51839 4.23744 8.11119 3.89113C7.88145 3.69572 7.66728 3.49917 7.48251 3.32247L7.17204 3.66397C7.15841 3.67897 7.03558 3.81356 6.83682 4.01523C7.03494 4.20517 7.26665 4.41823 7.51805 4.63239C7.96757 5.01527 8.3876 5.32218 8.76648 5.54456C9.28858 5.851 9.73206 5.99997 10.1222 5.99997C10.5105 5.99997 10.8874 5.92056 11.2425 5.76402C11.5853 5.61289 11.893 5.39665 12.1571 5.12128C12.4212 4.84591 12.6287 4.52514 12.7737 4.16786C12.9238 3.7977 13 3.40476 13 2.99997C13 2.59519 12.9238 2.20226 12.7737 1.83211Z"
                fill="currentColor"
              />
            </svg>

            <p className="text-[12px] font-semibold tabular-nums">
              {item?.restarted}
            </p>

            {IsChaseOverSix(item) ? (
              <p className="text-[12px] font-semibold tabular-nums">6</p>
            ) : null}
          </div>
          <div className="flex items-center justify-start gap-2.5">
            <svg
              className="text-light-gray"
              width="14"
              height="14"
              viewBox="0 0 11 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.21208 1.59883H1.81945L2.50848 0.909789C2.71631 0.701426 2.71631 0.364635 2.50848 0.156272C2.30012 -0.0520907 1.9628 -0.0520907 1.75497 0.156272L0.156272 1.75497C-0.0520907 1.96333 -0.0520907 2.30012 0.156272 2.50848L1.75497 4.10717C1.85888 4.21109 1.9953 4.26331 2.13172 4.26331C2.26815 4.26331 2.40457 4.21109 2.50848 4.10717C2.71631 3.89881 2.71631 3.56202 2.50848 3.35366L1.81945 2.66462H8.21208C8.67944 2.66462 9.05939 3.03072 9.05939 3.47942V4.79621C9.05939 5.0909 9.2976 5.32911 9.59229 5.32911C9.88698 5.32911 10.1252 5.0909 10.1252 4.79621V3.47942C10.1252 2.4424 9.26669 1.59883 8.21208 1.59883Z"
                fill="currentColor"
              />
              <path
                d="M8.37022 6.55104C8.16186 6.34267 7.82507 6.34267 7.61671 6.55104C7.40834 6.7594 7.40834 7.09619 7.61671 7.30455L8.30574 7.99359H1.9131C1.44575 7.99359 1.0658 7.62802 1.0658 7.17879V5.862C1.0658 5.56731 0.827057 5.3291 0.532898 5.3291C0.238738 5.3291 0 5.56731 0 5.862V7.17879C0 8.21581 0.857965 9.05938 1.9131 9.05938H8.30574L7.61671 9.74842C7.40834 9.95678 7.40834 10.2936 7.61671 10.5019C7.72062 10.6059 7.85704 10.6581 7.99346 10.6581C8.12989 10.6581 8.26631 10.6059 8.37022 10.5019L9.96892 8.90325C10.1773 8.69488 10.1773 8.35809 9.96892 8.14973L8.37022 6.55104Z"
                fill="currentColor"
              />
            </svg>

            {item?.partialPaymentAmount ? (
              <svg
                className="text-emerald-600 dark:text-emerald-300"
                width="14"
                height="9"
                viewBox="0 0 13 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M5.66021 7.86006L4.54062 8.97964L0.62207 5.06109L1.74166 3.94151L5.66021 7.86006ZM11.2581 0.0229492L12.3777 1.14254L5.66021 7.86006L4.54062 6.74046L11.2581 0.0229492Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                className="text-red-600 dark:text-red-300"
                width="10"
                height="10"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.38552 0.392699L7.99966 6.00684L13.5847 0.421788C13.7081 0.290477 13.8567 0.185433 14.0216 0.112955C14.1866 0.0404768 14.3645 0.00205708 14.5446 0C14.9304 0 15.3003 0.153235 15.5731 0.425996C15.8459 0.698756 15.9991 1.0687 15.9991 1.45444C16.0025 1.63276 15.9694 1.80989 15.9019 1.97496C15.8343 2.14003 15.7338 2.28956 15.6064 2.41437L9.94861 7.99943L15.6064 13.6572C15.8461 13.8917 15.9867 14.2093 15.9991 14.5444C15.9991 14.9302 15.8459 15.3001 15.5731 15.5729C15.3003 15.8456 14.9304 15.9989 14.5446 15.9989C14.3593 16.0065 14.1743 15.9756 14.0016 15.908C13.8288 15.8404 13.672 15.7376 13.5411 15.6062L7.99966 9.99201L2.40006 15.5916C2.27717 15.7185 2.13036 15.8199 1.9681 15.8898C1.80584 15.9597 1.63134 15.9967 1.45468 15.9989C1.06894 15.9989 0.698993 15.8456 0.426232 15.5729C0.153472 15.3001 0.000236647 14.9302 0.000236647 14.5444C-0.00315437 14.3661 0.0299297 14.189 0.0974587 14.0239C0.164988 13.8588 0.265537 13.7093 0.392936 13.5845L6.05071 7.99943L0.392936 2.34165C0.153222 2.10713 0.012657 1.78956 0.000236647 1.45444C0.000236647 1.0687 0.153472 0.698756 0.426232 0.425996C0.698993 0.153235 1.06894 0 1.45468 0C1.80374 0.00436332 2.13827 0.145444 2.38552 0.392699Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </div>
        </td>
        <td className="py-3.5 text-light-gray w-[12%] align-middle">
          {(() => {
            const status = getStatus(isTemporaryStatus || item?.status);
            if (!status) return null;
            return (
              <div
                className={`inline-flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-full ${status.bgColor}`}
              >
                <FontAwesomeIcon
                  icon={status.icon}
                  className={clsx("text-[11px]", status.color)}
                />

                <p
                  className={`text-[11px] font-semibold whitespace-nowrap ${status.color}`}
                >
                  {status.name?.split("-")[0].length > 4
                    ? status.name?.split("-")[0]
                    : status.name}
                </p>
              </div>
            );
          })()}
        </td>
        <td className="py-3.5 align-middle relative">
          <div className="flex items-center justify-end gap-0.5">
            <p className="text-[13px] leading-none mr-1">
              {item?.createdFrom === "M"
                ? "📄"
                : item?.createdFrom === "A"
                ? "🤖"
                : null}
            </p>
            <button
              type="button"
              title="Notes & messages"
              className="cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200"
              onClick={ToggleMessagesPopup}
            >
              <FontAwesomeIcon icon={faEnvelope} className="text-[15px]" />
            </button>
            <Menu as="div" className="relative inline-block text-left">
            <div>
              <Menu.Button
                data-bs-toggle="dropdown"
                title="More actions"
                className="cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faEllipsisV} className="text-[15px]" />
              </Menu.Button>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <div className="dropdown-menu p-[0] bg-transparent border-none absolute top-[20px] right-0 z-[99999]">
                <div
                  className={clsx(
                    "z-10 mt-2 w-[auto] min-w-[190px] rounded-xl bg-surface-elevated border border-edge shadow-overlay overflow-hidden"
                  )}
                >
                  <div>
                    {!item?.archived ? (
                      <>
                        {chaseTableActions.map((action, index) => {
                          return (
                            <div
                              key={index}
                              title={
                                action.disabled
                                  ? `${action.name} is currently disabled`
                                  : undefined
                              }
                              className={clsx(
                                "flex gap-3 px-4 py-2.5 text-[13px] text-text-primary items-center font-medium transition-colors",
                                action.disabled
                                  ? "opacity-40 cursor-not-allowed"
                                  : "cursor-pointer hover:bg-overlay-hover"
                              )}
                              onClick={() => {
                                if (action.disabled) return;
                                action.name === "Edit Chase"
                                  ? HandleEditChase()
                                  : onOpenDeletePopup({
                                      id: item.id,
                                      onClick: HandleDeleteChase,
                                    });
                              }}
                            >
                              <FontAwesomeIcon
                                icon={action?.icon}
                                className="text-[13px] text-text-primary w-4 shrink-0"
                              />
                              {action.name}
                            </div>
                          );
                        })}

                        {getStatus(isTemporaryStatus || item?.status)?.name ===
                        "Plan" ? (
                          <div
                            className="flex gap-3 px-4 py-2.5 text-[13px] text-text-primary items-center font-medium cursor-pointer hover:bg-overlay-hover transition-colors"
                            onClick={CancelPaymentPlan}
                          >
                            <FontAwesomeIcon
                              icon={faXmarkCircle}
                              className="text-[13px] text-text-primary w-4 shrink-0"
                            />
                            Cancel Plan
                          </div>
                        ) : null}

                        {user?.type !== "agent" &&
                        getStatus(isTemporaryStatus || item?.status)?.name ===
                          "Debt-Collectors" ? (
                          <div
                            className="flex gap-3 px-4 py-2.5 text-[13px] text-text-primary items-center font-medium cursor-pointer hover:bg-overlay-hover transition-colors"
                            onClick={RemoveChaseFromICB}
                          >
                            <FontAwesomeIcon
                              icon={faXmarkCircle}
                              className="text-[13px] text-text-primary w-4 shrink-0"
                            />
                            Remove From ICB
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    <div
                      className="flex gap-3 px-4 py-2.5 text-[13px] text-text-primary items-center font-medium cursor-pointer hover:bg-overlay-hover transition-colors border-t border-edge-faint"
                      onClick={() => {
                        onOpenSuretyPopup({
                          id: item?.id,
                          onClick: HandletoggleArchive,
                          title: `Are you sure you want to ${
                            item?.archived ? "unarchive" : "archive"
                          } this chase?`,
                          description: item?.archived
                            ? "It returns to the active chase list and starts sending again from where it left off."
                            : "It moves to Customer Archives, stops sending, and its history is parked. You can unarchive it later.",
                          confirmLabel: item?.archived
                            ? "Unarchive"
                            : "Archive",
                        });
                      }}
                    >
                      <FontAwesomeIcon icon={faBook} className="text-[13px] text-text-primary w-4 shrink-0" />
                      {item?.archived ? "Unarchive" : "Archive"} Chase
                    </div>

                    {item?.archived ? (
                      <div
                        className="flex gap-3 px-4 py-2.5 text-[13px] text-red-600 dark:text-red-400 items-center font-medium cursor-pointer hover:bg-overlay-hover transition-colors border-t border-edge-faint"
                        onClick={() =>
                          onOpenDeletePopup({
                            id: item.id,
                            onClick: HandleDeleteChase,
                          })
                        }
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          className="text-[13px] text-red-600 dark:text-red-400 w-4 shrink-0"
                        />
                        Delete Chase
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </Transition>
            </Menu>

            <button
              type="button"
              title="Expand"
              aria-expanded={initialValues?.id === item?.id}
              onClick={() =>
                onToggleChaseMenu({
                  id: item?.id,
                  data: item,
                  timeline: item?.timeline,
                  logs: item?.logs,
                })
              }
              className="cursor-pointer h-7 w-7 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200"
            >
              <FontAwesomeIcon
                icon={faChevronDown}
                className={clsx(
                  "text-[13px] transition-transform duration-200",
                  initialValues?.id === item?.id && "rotate-180",
                )}
              />
            </button>
          </div>
        </td>
      </tr>
      <tr>
        {initialValues?.id === item?.id && <ChaseDropMenu />}
      </tr>
      <tr>
        <td>
          {deletePopupInitialValues?.id === item?.id && (
            <DeleteModel loading={loading} />
          )}
        </td>
      </tr>
      <tr>
        <td>
          {suretyInitialValues?.id === item?.id && (
            <PopupSuretyModel loading={loading} />
          )}
        </td>
      </tr>
      <tr>
        <td>
          {editInitialValues.id === item?.id && (
            <EditChaseModel UpdateItemData={UpdateItemData} />
          )}
        </td>
      </tr>

      {isMessagePopup ? (
        <NotesModal
          onToggle={ToggleMessagesPopup}
          data={{ policyNumber: item?.policyReference }}
        />
      ) : null}
    </>
  );
};

export default ChaseRenderitem;
